import type {
  UpdateNotificationSettings,
  XcNotificationSettingRead,
} from '@capital-markets-gateway/api-client-identity';
import { accessTokenExpirationRequestInterceptor } from '@cmg/auth';
import type { UnpackPromise } from '@cmg/common';
import { apiUtil, urlUtil } from '@cmg/common';
import type { AxiosResponse } from 'axios';
import axios from 'axios';

import type { ConsentInfo, ConsentResponseCode } from '../../types/domain/consent/consentInfo';
import type { ErrorInfo } from '../../types/domain/error/errorInfo';
import type { LoginInfo } from '../../types/domain/login-info/loginInfo';
import type { ChangePassword } from '../../types/domain/self/ChangePassword';
import type { PasswordReset } from '../../types/domain/self/PasswordReset';
import type { Profile, ProfileUpdate } from '../../types/domain/self/profile';
import type { ActivateUser } from '../../types/domain/self-settings/ActivateUser';
import { accessTokenRequestInterceptor, responseErrorInterceptor } from './interceptors';

/**
 * The identity client
 **/
const client = axios.create({
  baseURL: '/api/v1',
  responseType: 'json',
  headers: {
    'Content-Type': 'application/json',
  },
});

client.interceptors.request.use(accessTokenExpirationRequestInterceptor);
client.interceptors.request.use(accessTokenRequestInterceptor);
client.interceptors.response.use(undefined, responseErrorInterceptor);

/**
 * API Calls
 */
export enum LoginUserPassResponseCode {
  SUCCESS = 'SUCCESS',
  REQUIRE_TWO_FACTOR = 'REQUIRE_TWO_FACTOR',
  PASSWORD_EXPIRED = 'PASSWORD_EXPIRED',
  LOCKED_OUT = 'LOCKED_OUT',
  INVALID_CREDENTIALS = 'INVALID_CREDENTIALS',
  USER_INACTIVE = 'USER_INACTIVE',
  ACCOUNT_DISABLED = 'ACCOUNT_DISABLED',
  TRIAL_EXPIRED = 'TRIAL_EXPIRED',
  NOT_ALLOWED = 'NOT_ALLOWED',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  NOT_FOUND = 'NOT_FOUND',
  IP_BLOCKED = 'IP_BLOCKED',
  EDIT_CONFLICT = 'EDIT_CONFLICT',
  DUPLICATE = 'DUPLICATE',
  UNHANDLED_ERR0R = 'UNHANDLED_ERROR',
  NETWORK_FAILURE = 'NETWORK_FAILURE',
  FORBIDDEN = 'FORBIDDEN',
}

/** Grant and deny both succeed with a `returnUrl` to navigate to. */
export type ConsentSuccessBody = {
  code: ConsentResponseCode.GRANTED | ConsentResponseCode.DENIED;
  returnUrl: string;
  message?: string;
};

/**
 * 400 on either consent endpoint: the `returnUrl` expired, was not an authorize callback, or its
 * client document no longer resolves. One code, because the screen is the same in every case.
 */
export type ConsentFailureBody = {
  code?: ConsentResponseCode.INVALID_REQUEST;
  message?: string;
};

type LoginUserPassSuccessBody = {
  code: LoginUserPassResponseCode.SUCCESS | LoginUserPassResponseCode.REQUIRE_TWO_FACTOR;
  message: string;
  returnUrl: string;
};

type MFALoginUserPassSuccessBody = {
  code: LoginUserPassResponseCode.SUCCESS;
  message: string;
  returnUrl: string;
};

export type LoginUserPassPasswordExpiredError = {
  code: LoginUserPassResponseCode.PASSWORD_EXPIRED;
  message: string;
  token: string;
  userId: string;
};

export type LoginUserPassFailureBody =
  | {
      code:
        | LoginUserPassResponseCode.INVALID_CREDENTIALS
        | LoginUserPassResponseCode.LOCKED_OUT
        | LoginUserPassResponseCode.USER_INACTIVE
        | LoginUserPassResponseCode.ACCOUNT_DISABLED
        | LoginUserPassResponseCode.TRIAL_EXPIRED
        | LoginUserPassResponseCode.NOT_ALLOWED;
      message: string;
    }
  | {
      code: LoginUserPassResponseCode.REQUIRE_TWO_FACTOR;
      message: string;
      sharedKey: string;
      authenticatorUri: string;
      firstTimeSetup: boolean;
    }
  | LoginUserPassPasswordExpiredError;

export type MFALoginUserPassFailureBody =
  | {
      code:
        | LoginUserPassResponseCode.VALIDATION_ERROR
        | LoginUserPassResponseCode.NOT_FOUND
        | LoginUserPassResponseCode.EDIT_CONFLICT
        | LoginUserPassResponseCode.INVALID_CREDENTIALS
        | LoginUserPassResponseCode.LOCKED_OUT
        | LoginUserPassResponseCode.PASSWORD_EXPIRED
        | LoginUserPassResponseCode.DUPLICATE
        | LoginUserPassResponseCode.IP_BLOCKED
        | LoginUserPassResponseCode.UNHANDLED_ERR0R
        | LoginUserPassResponseCode.NETWORK_FAILURE
        | LoginUserPassResponseCode.FORBIDDEN;
      message: string;
    }
  | {
      code: LoginUserPassResponseCode.REQUIRE_TWO_FACTOR;
      message: string;
      sharedKey: string;
      authenticatorUri: string;
      firstTimeSetup: boolean;
    }
  | LoginUserPassPasswordExpiredError;

/**
 * Login to the identity service with username and password
 */
const loginUserPass = (params: { username: string; password: string; returnUrl: string }) =>
  client
    .post<typeof params, AxiosResponse<LoginUserPassSuccessBody>>('/login', {
      username: params.username,
      password: params.password,
      // rememberLogin intentionally disabled until backend confirms behavior
      returnUrl: params.returnUrl,
    })
    .then(apiUtil.transformSuccessResponse)
    .catch(apiUtil.makeTransformFailureResponse<LoginUserPassFailureBody>());
export type LoginUserPassResponse = UnpackPromise<ReturnType<typeof loginUserPass>>;

/**
 * MFALogin to the identity service with securityCode and rememberDevice
 */
const mfaLoginUserPass = (params: { code: string; rememberDevice: boolean; returnUrl: string }) =>
  client
    .post<typeof params, AxiosResponse<MFALoginUserPassSuccessBody>>('/verifyCode', {
      code: params.code,
      rememberDevice: params.rememberDevice,
      returnUrl: params.returnUrl,
    })
    .then(apiUtil.transformSuccessResponse)
    .catch(apiUtil.makeTransformFailureResponse<MFALoginUserPassFailureBody>());
export type MFALoginUserPassResponse = UnpackPromise<ReturnType<typeof mfaLoginUserPass>>;

/**
 * Submits a Forgot Password request which will send the user an email with a reset password link
 */
const sendPasswordResetEmail = (email: string) => {
  return client
    .post<{ email: string }, AxiosResponse<void>>('/self/password/resetRequests', { email })
    .then(apiUtil.transformSuccessResponse)
    .catch(apiUtil.transformFailureResponse);
};
export type ResetPasswordRequestResponse = UnpackPromise<ReturnType<typeof sendPasswordResetEmail>>;

/**
 * Resets a password with a token that was provided from the forgot password email or an login failure due to an expired password
 */
const resetPassword = (params: PasswordReset) => {
  return client
    .put<PasswordReset, AxiosResponse<void>>('/self/password/reset', params)
    .then(apiUtil.transformSuccessResponse)
    .catch(apiUtil.transformFailureResponse);
};
export type ResetPasswordResponse = UnpackPromise<ReturnType<typeof resetPassword>>;

/**
 * Activates a user with a token that was provided from the welcome email
 */
const activateUser = (params: ActivateUser) => {
  return client
    .post<ActivateUser, AxiosResponse<void>>('/self/profile/activate', params)
    .then(apiUtil.transformSuccessResponse)
    .catch(apiUtil.transformFailureResponse);
};
export type ActivateUserResponse = UnpackPromise<ReturnType<typeof activateUser>>;

/**
 * Activates a user with a token that was provided from the welcome email
 */
const changePassword = (params: ChangePassword) => {
  return client
    .put<ChangePassword, AxiosResponse<void>>('/self/password/change', params)
    .then(apiUtil.transformSuccessResponse)
    .catch(apiUtil.transformFailureResponse);
};
export type ChangePasswordResponse = UnpackPromise<ReturnType<typeof changePassword>>;

/**
 * Fetch login info
 * Contains the login information to determine which mechanisms the user can login with
 */
const fetchLoginInfo = (params: { returnUrl: string }) => {
  const queryString = urlUtil.queryStringify({
    returnUrl: params.returnUrl,
  });
  return client
    .get<LoginInfo>(`/loginInfo${queryString}`)
    .then(apiUtil.transformSuccessResponse)
    .catch(apiUtil.transformFailureResponse);
};
export type FetchLoginInfoResponse = UnpackPromise<ReturnType<typeof fetchLoginInfo>>;

/**
 * Fetch the OAuth consent context for an authorize request.
 * The `returnUrl` is the authorize callback Duende handed to the SPA; it is passed back verbatim.
 */
const fetchConsentInfo = (params: { returnUrl: string }) => {
  const queryString = urlUtil.queryStringify({
    returnUrl: params.returnUrl,
  });
  return client
    .get<ConsentInfo>(`/consentInfo${queryString}`)
    .then(apiUtil.transformSuccessResponse)
    .catch(apiUtil.makeTransformFailureResponse<ConsentFailureBody>());
};
export type FetchConsentInfoResponse = UnpackPromise<ReturnType<typeof fetchConsentInfo>>;

/**
 * Submit the user's consent decision.
 *
 * Grant and deny both come back as JSON carrying a server-validated `returnUrl` — never a 302, which
 * axios would follow inside the XHR and burn the authorization code. The caller navigates.
 */
const submitConsent = (params: { returnUrl: string; granted: boolean; rememberConsent: boolean }) =>
  client
    .post<typeof params, AxiosResponse<ConsentSuccessBody>>('/consent', {
      returnUrl: params.returnUrl,
      granted: params.granted,
      rememberConsent: params.rememberConsent,
    })
    .then(apiUtil.transformSuccessResponse)
    .catch(apiUtil.makeTransformFailureResponse<ConsentFailureBody>());
export type SubmitConsentResponse = UnpackPromise<ReturnType<typeof submitConsent>>;

/**
 * fetch user profile information
 * Contains profile details to be displayed in the profile page
 */
const fetchProfile = () => {
  return client
    .get<Profile>('/self/profile')
    .then(apiUtil.transformSuccessResponse)
    .catch(apiUtil.transformFailureResponse);
};
export type FetchProfileResponse = UnpackPromise<ReturnType<typeof fetchProfile>>;

/**
 * updates user profile information
 */
const updateProfile = (params: ProfileUpdate) => {
  return client
    .put<ProfileUpdate, AxiosResponse<Profile>>('/self/profile', params)
    .then(apiUtil.transformSuccessResponse)
    .catch(apiUtil.transformFailureResponse);
};
export type UpdateProfileResponse = UnpackPromise<ReturnType<typeof updateProfile>>;

/**
 * fetch XC notification settings
 * Contains XC notification settings for email subscriptions
 */
const fetchXcNotificationSettings = () => {
  return client
    .get<readonly XcNotificationSettingRead[]>('/self/profile/userSettings/xcNotifications')
    .then(apiUtil.transformSuccessResponse)
    .catch(apiUtil.transformFailureResponse);
};
export type FetchXcNotificationSettingsResponse = UnpackPromise<
  ReturnType<typeof fetchXcNotificationSettings>
>;

/**
 * updates XC notification settings
 */
const updateXcNotificationSettings = (params: readonly UpdateNotificationSettings[]) => {
  return client
    .put<
      readonly UpdateNotificationSettings[],
      AxiosResponse<readonly XcNotificationSettingRead[]>
    >('/self/profile/userSettings/xcNotifications', params)
    .then(apiUtil.transformSuccessResponse)
    .catch(apiUtil.transformFailureResponse);
};
export type UpdateXcNotificationSettingsResponse = UnpackPromise<
  ReturnType<typeof updateXcNotificationSettings>
>;

/**
 * fetch user profile information
 * Contains profile details to be displayed in the profile page
 */
const resendCode = () => {
  return client
    .post('/resendCode')
    .then(apiUtil.transformSuccessResponse)
    .catch(apiUtil.makeTransformFailureResponse<MFALoginUserPassFailureBody>());
};
export type ResendCodeResponse = UnpackPromise<ReturnType<typeof resendCode>>;

/**
 * Fetch the text for an error the app cannot resolve on its own.
 *
 * `errorId` is IdentityServer's message-store key, minted by the authorize endpoint and readable
 * only server-side. The application's own codes are resolved here in the SPA and never sent.
 * An id that resolves to nothing - expired, consumed, or never minted - answers 404, which the
 * caller renders as its own generic screen rather than an error the server never recorded.
 */
const fetchErrorInfo = (params: { errorId: string }) => {
  const queryString = urlUtil.queryStringify({ errorId: params.errorId });
  return client
    .get<ErrorInfo>(`/errorInfo${queryString}`)
    .then(apiUtil.transformSuccessResponse)
    .catch(apiUtil.transformFailureResponse);
};
export type FetchErrorInfoResponse = UnpackPromise<ReturnType<typeof fetchErrorInfo>>;

const identityApiClient = {
  loginUserPass,
  sendPasswordResetEmail,
  resetPassword,
  activateUser,
  changePassword,
  fetchLoginInfo,
  fetchConsentInfo,
  fetchErrorInfo,
  submitConsent,
  fetchProfile,
  updateProfile,
  fetchXcNotificationSettings,
  updateXcNotificationSettings,
  mfaLoginUserPass,
  resendCode,
};

export default identityApiClient;
